import store, { RootState } from "../../dataManagement/uiDb/UiDb";
import DineInThunk from "../thunks/DineInThunk";

export type DineInPushData = Record<string, string>;

type DineInPushHandler = (data: DineInPushData) => void;

/**
 * Per-event overrides for when "just refetch the session" isn't enough —
 * e.g. a future event that should also navigate somewhere, or introduce a
 * data shape the session envelope doesn't carry (a generated bill, say).
 * Empty today: every event type on the current roadmap (item added, member
 * joined, round ordered, quantity updated) resolves to data the session
 * envelope already contains, so defaultHandler covers all of them with zero
 * entries here. Add a key ONLY when a specific type needs more than a
 * refetch — don't pre-populate this for types that don't exist yet.
 */
const DINE_IN_PUSH_HANDLERS: Partial<Record<string, DineInPushHandler>> = {};

// One in-flight guard + one pending flag per sessionId, so a burst of events
// for the same table (three people adding items back to back) collapses
// into a single request in flight and at most one trailing refetch — never
// a pile of parallel GETs. Keyed by sessionId rather than a single boolean
// in case more than one session's data is ever held at once.
const inFlight = new Set<string>();
const pending = new Set<string>();

/**
 * Debounced snapshot refetch for one session — exported so both callers that
 * need it share the same guard: a DINE_IN_* push (defaultHandler below) and
 * useDineInLiveSync's focus/visibility resync. Without sharing this, a push
 * landing at the exact moment a backgrounded tab regains focus would fire
 * two parallel GETs instead of collapsing into one.
 */
export async function requestDineInRefresh(sessionId: string): Promise<void> {
  if (inFlight.has(sessionId)) {
    pending.add(sessionId);
    return;
  }
  inFlight.add(sessionId);
  try {
    await store.dispatch(DineInThunk.refreshDineInSession({ sessionId }));
  } finally {
    inFlight.delete(sessionId);
    if (pending.delete(sessionId)) {
      // A newer request for this session arrived mid-flight — the snapshot
      // we just applied may already be stale, so run once more.
      requestDineInRefresh(sessionId);
    }
  }
}

/**
 * Default handler for any DINE_IN_* push not in the registry above: refetch
 * the session snapshot, but only when it's the session currently held in
 * DineInDb — a push for a table this tab isn't tracking has nothing to
 * refresh (the alert/toast side of the push already fired unconditionally
 * in handleIncomingPush before this is ever reached).
 */
function defaultHandler(data: DineInPushData): void {
  const sessionId = data.sessionId;
  if (!sessionId) {
    console.warn("[dineInPushRouter] push had no sessionId, ignoring:", data);
    return;
  }
  const state = store.getState() as RootState;
  const activeSessionId = state.dineIn.session?.sessionId;
  if (activeSessionId !== sessionId) {
    console.log(
      `[dineInPushRouter] push for session ${sessionId} but active session is ${activeSessionId ?? "none"} — ignoring`
    );
    return;
  }
  console.log(`[dineInPushRouter] refreshing session ${sessionId} (type: ${data.type})`);
  void requestDineInRefresh(sessionId);
}

/**
 * Entry point for any push whose `type` starts with "DINE_IN_" — called from
 * handleIncomingPush.ts ahead of its chat-message logic (a dine-in push is
 * never a chat message). Looks up a specific handler for this exact type;
 * falls back to defaultHandler for anything unrecognized. That fallback is
 * the whole point: a brand-new event type the backend starts sending
 * tomorrow (round ordered, quantity updated, member joined, ...) is handled
 * correctly the moment it arrives — no client change required to "accommodate"
 * it, exactly the scalability goal this router exists for.
 */
export function handleDineInPush(data: DineInPushData): void {
  console.log("[dineInPushRouter] handling push:", data);
  const handler = (data.type && DINE_IN_PUSH_HANDLERS[data.type]) || defaultHandler;
  handler(data);
}
